import {
  createFolderUploadJob,
  retryableFolderUploadFileIds,
} from './reducers';
import { createFolderUploadRunner, type FolderUploadRunner } from './runner';
import type {
  FolderUploadDeps,
  FolderUploadLimits,
  FolderUploadPlan,
} from './runner-types';
import { createFolderUploadStore, type FolderUploadStore } from './store';

export type FolderUploadManager = ReturnType<typeof createFolderUploadManager>;

let jobSequence = 0;

/**
 * Starts folder uploads and routes the panel's Cancel and Retry buttons to
 * the runner of each job. Lives for the whole page, outside any component.
 */
export function createFolderUploadManager(
  store: FolderUploadStore = createFolderUploadStore(),
  now: () => number = Date.now
) {
  const runners = new Map<string, FolderUploadRunner>();
  return {
    store,
    /** Adds the job to the panel and starts it; `done` settles when it stops. */
    start(
      plan: FolderUploadPlan,
      deps: FolderUploadDeps,
      limits: FolderUploadLimits
    ): { jobId: string; done: Promise<void> } {
      jobSequence += 1;
      const jobId = `folder-upload-${now().toString(36)}-${jobSequence}`;
      store.add(
        createFolderUploadJob({
          id: jobId,
          orgSlug: plan.orgSlug,
          targetDirectoryPath: plan.targetDirectoryPath,
          selectionKind: plan.selectionKind,
          files: plan.files.map((file) => ({
            name: file.name,
            relativeDirectoryPath: file.relativeDirectoryPath,
            sizeBytes: file.file.size,
            failure: file.failure ? { reason: file.failure } : null,
          })),
          blockedReason: plan.blockedReason,
          emptyFolderCount: plan.emptyDirectories.length,
          now: now(),
        })
      );
      const runner = createFolderUploadRunner({
        jobId,
        plan,
        deps,
        limits,
        store,
        now,
      });
      runners.set(jobId, runner);
      return { jobId, done: runner.start() };
    },
    cancelAll(jobId: string) {
      runners.get(jobId)?.cancelAll();
    },
    cancelFile(jobId: string, fileId: number) {
      runners.get(jobId)?.cancelFile(fileId);
    },
    retryFailed(jobId: string) {
      const job = store.get(jobId);
      if (!job) return Promise.resolve();
      return (
        runners.get(jobId)?.retry(retryableFolderUploadFileIds(job)) ??
        Promise.resolve()
      );
    },
    retryFile(jobId: string, fileId: number) {
      return runners.get(jobId)?.retry([fileId]) ?? Promise.resolve();
    },
    /** Removes a finished job from the panel; a running one stays. */
    dismiss(jobId: string) {
      if (store.get(jobId)?.running) return;
      runners.delete(jobId);
      store.remove(jobId);
    },
  };
}

/** The one manager the app's upload actions and panel share. */
export const folderUploadManager = createFolderUploadManager();
